import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  EyeIcon,
  PencilIcon,
  TrashIcon,
  BriefcaseIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { formatDate, formatDateShort } from "@/shared/core";
import type { CaseWithDetails } from "../types/case.types";

interface CasesTableProps {
  cases: CaseWithDetails[];
  onView: (caseId: string) => void;
  onEdit: (caseData: CaseWithDetails) => void;
  onDelete: (caseId: string, caseTitle: string) => void;
  loading?: boolean;
}

type SortField =
  | "title"
  | "case_number"
  | "client"
  | "status"
  | "priority"
  | "practice_area"
  | "date";
type SortDirection = "asc" | "desc";

export const CasesTable: React.FC<CasesTableProps> = ({
  cases,
  onView,
  onEdit,
  onDelete,
  loading = false,
}) => {
  const { t } = useTranslation();
  const [sortField, setSortField] = useState<SortField>("date");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const sortedCases = [...cases].sort((a, b) => {
    let compareA: string | number | Date;
    let compareB: string | number | Date;

    switch (sortField) {
      case "title":
        compareA = a.title?.toLowerCase() || "";
        compareB = b.title?.toLowerCase() || "";
        break;
      case "case_number":
        compareA = a.case_number?.toLowerCase() || "";
        compareB = b.case_number?.toLowerCase() || "";
        break;
      case "client":
        compareA = a.client_profiles?.profiles?.full_name?.toLowerCase() || "";
        compareB = b.client_profiles?.profiles?.full_name?.toLowerCase() || "";
        break;
      case "status":
        compareA = a.status;
        compareB = b.status;
        break;
      case "priority": {
        const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 };
        compareA = priorityOrder[a.priority as keyof typeof priorityOrder] || 0;
        compareB = priorityOrder[b.priority as keyof typeof priorityOrder] || 0;
        break;
      }
      case "practice_area":
        compareA = a.practice_area?.toLowerCase() || "";
        compareB = b.practice_area?.toLowerCase() || "";
        break;
      case "date":
        compareA = new Date(a.created_at);
        compareB = new Date(b.created_at);
        break;
      default:
        return 0;
    }

    if (compareA < compareB) return sortDirection === "asc" ? -1 : 1;
    if (compareA > compareB) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            {t("lawyer.cases.status.pending")}
          </span>
        );
      case "active":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-atlantis-100 text-atlantis-800">
            {t("lawyer.cases.status.active")}
          </span>
        );
      case "closed":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-tiber-100 text-tiber-800">
            {t("lawyer.cases.status.closed")}
          </span>
        );
      case "archived":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-tiber-100 text-tiber-800">
            {t("lawyer.cases.status.archived")}
          </span>
        );
      default:
        return null;
    }
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case "urgent":
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
            {t("lawyer.cases.priority.urgent")}
          </span>
        );
      case "high":
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
            {t("lawyer.cases.priority.high")}
          </span>
        );
      case "medium":
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-tiber-100 text-tiber-800">
            {t("lawyer.cases.priority.medium")}
          </span>
        );
      case "low":
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-tiber-100 text-tiber-800">
            {t("lawyer.cases.priority.low")}
          </span>
        );
      default:
        return null;
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null;
    return sortDirection === "asc" ? (
      <ChevronUpIcon className="w-4 h-4 inline ml-1" />
    ) : (
      <ChevronDownIcon className="w-4 h-4 inline ml-1" />
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-lg border border-tiber-600 p-8 text-center">
        <div className="animate-pulse">
          <div className="h-8 bg-tiber-200 rounded w-full mb-4"></div>
          <div className="h-8 bg-tiber-200 rounded w-full mb-4"></div>
          <div className="h-8 bg-tiber-200 rounded w-full mb-4"></div>
        </div>
      </div>
    );
  }

  if (cases.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-lg border border-tiber-600 p-12 text-center">
        <BriefcaseIcon className="h-12 w-12 text-titan mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-juridico mb-2">
          {t("lawyer.cases.empty.title")}
        </h3>
        <p className="text-tiber-700">
          {t(
            "lawyer.cases.empty.description",
            "Get started by creating your first case"
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg border border-tiber-600 overflow-hidden min-w-0">
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="min-w-full divide-y divide-tiber-200">
          <thead className="bg-romance-50">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-titan uppercase tracking-wider cursor-pointer hover:bg-tiber-100"
                onClick={() => handleSort("title")}
              >
                {t("lawyer.cases.table.title")}
                <SortIcon field="title" />
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-titan uppercase tracking-wider cursor-pointer hover:bg-tiber-100"
                onClick={() => handleSort("case_number")}
              >
                {t("lawyer.cases.table.caseNumber")}
                <SortIcon field="case_number" />
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-titan uppercase tracking-wider cursor-pointer hover:bg-tiber-100"
                onClick={() => handleSort("client")}
              >
                {t("lawyer.cases.table.client")}
                <SortIcon field="client" />
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-titan uppercase tracking-wider cursor-pointer hover:bg-tiber-100"
                onClick={() => handleSort("status")}
              >
                {t("lawyer.cases.table.status")}
                <SortIcon field="status" />
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-titan uppercase tracking-wider cursor-pointer hover:bg-tiber-100"
                onClick={() => handleSort("priority")}
              >
                {t("lawyer.cases.table.priority")}
                <SortIcon field="priority" />
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-titan uppercase tracking-wider cursor-pointer hover:bg-tiber-100"
                onClick={() => handleSort("practice_area")}
              >
                {t("lawyer.cases.table.practiceArea")}
                <SortIcon field="practice_area" />
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-titan uppercase tracking-wider cursor-pointer hover:bg-tiber-100"
                onClick={() => handleSort("date")}
              >
                {t("lawyer.cases.table.date")}
                <SortIcon field="date" />
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-right text-xs font-medium text-titan uppercase tracking-wider"
              >
                {t("lawyer.cases.table.actions")}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-tiber-200">
            {sortedCases.map((case_, index) => (
              <motion.tr
                key={case_.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="hover:bg-romance-50 transition-colors"
              >
                <td className="px-6 py-4">
                  <div className="flex items-center">
                    <div className="shrink-0 h-10 w-10 rounded-lg bg-linear-to-r from-atlantis-500 to-atlantis-600 flex items-center justify-center">
                      <BriefcaseIcon className="h-5 w-5 text-white" />
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-juridico">
                        {case_.title}
                      </div>
                      {case_.description && (
                        <div className="text-sm text-titan truncate max-w-xs">
                          {case_.description}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm font-mono text-juridico">
                    {case_.case_number}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-juridico">
                    {case_.client_profiles?.profiles?.full_name || "N/A"}
                  </div>
                  {case_.client_profiles?.profiles?.email && (
                    <div className="text-sm text-titan">
                      {case_.client_profiles.profiles.email}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getStatusBadge(case_.status)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getPriorityBadge(case_.priority)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm text-juridico capitalize">
                    {t(
                      `lawyer.cases.practiceAreas.${case_.practice_area}`,
                      case_.practice_area
                    )}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-juridico">
                    {formatDateShort(case_.created_at)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => onView(case_.id)}
                      className="text-tiber-600 hover:text-tiber-900 p-1 rounded hover:bg-tiber-50 transition-colors"
                      title={t("lawyer.cases.actions.view")}
                    >
                      <EyeIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => onEdit(case_)}
                      className="text-tiber-600 hover:text-tiber-900 p-1 rounded hover:bg-tiber-50 transition-colors"
                      title={t("lawyer.cases.actions.edit")}
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => onDelete(case_.id, case_.title)}
                      className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 transition-colors"
                      title={t("lawyer.cases.actions.delete")}
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden divide-y divide-tiber-200">
        {sortedCases.map((case_, index) => (
          <motion.div
            key={case_.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="p-4 hover:bg-romance-50"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center flex-1 min-w-0">
                <div className="shrink-0 h-12 w-12 rounded-lg bg-linear-to-r from-atlantis-500 to-atlantis-600 flex items-center justify-center mr-3">
                  <BriefcaseIcon className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-juridico truncate">
                    {case_.title}
                  </div>
                  <div className="text-xs font-mono text-titan mt-1">
                    {case_.case_number}
                  </div>
                </div>
              </div>
              {getStatusBadge(case_.status)}
            </div>
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              {getPriorityBadge(case_.priority)}
              <span className="text-xs text-titan capitalize">
                {t(
                  `lawyer.cases.practiceAreas.${case_.practice_area}`,
                  case_.practice_area
                )}
              </span>
            </div>
            <div className="text-sm text-tiber-700 mb-3">
              <div className="truncate">
                {t("lawyer.cases.table.client")}:{" "}
                {case_.client_profiles?.profiles?.full_name || "N/A"}
              </div>
              <div className="text-xs text-titan mt-1">
                {formatDate(case_.created_at)}
              </div>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => onView(case_.id)}
                className="flex-1 px-3 py-2 bg-tiber-50 text-tiber-700 rounded-lg hover:bg-tiber-100 transition-colors text-sm font-medium"
              >
                {t("lawyer.cases.actions.view")}
              </button>
              <button
                onClick={() => onEdit(case_)}
                className="flex-1 px-3 py-2 bg-tiber-50 text-tiber-700 rounded-lg hover:bg-tiber-100 transition-colors text-sm font-medium"
              >
                {t("lawyer.cases.actions.edit")}
              </button>
              <button
                onClick={() => onDelete(case_.id, case_.title)}
                className="px-3 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors"
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};
